<script lang="ts">
export default {
  name: 'CompaniesBenefitsDetail',
}
</script>

<script setup lang="ts">
import {computed, ref} from 'vue'
import { KkBox, KkButton, KkIcon, KkLoading} from 'src/biz_spa/components/base'
import {useI18n} from 'vue-i18n'
import {useCompaniesBenefit} from 'src/biz_spa/domains/benefits/stores'
import { KkApplyModal } from '../../../domains/benefits'
import {storeToRefs} from 'pinia'
import {useRoute} from 'vue-router'
import {useStore} from 'vuex'

interface Emits {
  (e: 'update-title'): void
  (e: 'update-progress'): void
  (e: 'update-breadcrumbs'): void
}
defineEmits<Emits>()

type Brand ={
  attributes: {
    name: string,
    store_count: number,
    business_type_id: number
  }
}

interface Content {
  id: number
  title: string
  text: string
  notes?: readonly string[]
  dueDate?: string
  applicationUrl?: string
}

const { t } = useI18n()
const route = useRoute()
const benefitStore = useCompaniesBenefit()
const {
  benefit,
  isLoading
} = storeToRefs(benefitStore)
const {
  applyCompaniesBenefit,
  readCompaniesBenefit
} = benefitStore
const store = useStore()

const companyUuid = computed<string>(() => route.params.company_uuid as string)
const benefitId = computed<number>(() => parseInt(route.params.benefit_id as string, 10))
const benefitBusinessTypeIds = computed<number[]>(() => benefit.value.targetBusinessTypes.map(v => v.id))
const brands = computed<Brand[]>(() => store.state.companiesBrands.data)
const targetBrands = computed<Brand[]>(() => brands.value.filter((brand => brand.attributes.store_count !== 0 && benefitBusinessTypeIds.value.includes(brand.attributes.business_type_id))))
const targetBrandBusinessTypeIds = computed<number[]>(() => targetBrands.value.map(brand => brand.attributes.business_type_id))
const targetBrandBusinessTypeNames = computed<string[]>(() => benefit.value.targetBusinessTypes.filter(type => targetBrandBusinessTypeIds.value.includes(type.id)).map(v => v.name))
const brandLoading = computed<boolean>(() => store.state.companiesBrands.loading)
const company = computed(() => store.getters['companiesCompany/company'])
const isDocomoPlan = computed(() => store.getters['companiesCompany/isDocomoPlan'])
const currentUserEmail = computed<string>(() => gon.currentUser.email)
const currentUserName = computed<string>(() => gon.currentUser.name)

const isApplyModalVisible = ref<boolean>(false)
const targetContent = ref<Content>()

const handleOpenApplyModal = (content: Content): void => {
  targetContent.value = content
  isApplyModalVisible.value = true
}

const handleApply = (phoneNumber: string): void => {
  if(!targetContent.value) return
  if(isDocomoPlan.value) {
    window.open('https://kutikomi.com/contact?kind=introduction', '_blank')
    isApplyModalVisible.value = false
    return
  }
  if(targetContent.value.applicationUrl) {
    window.open(targetContent.value.applicationUrl, '_blank')
  }

  applyCompaniesBenefit({
    companyUuid: companyUuid.value,
    benefitId: benefitId.value,
    contentId: targetContent.value.id,
    params: {phoneNumber},
    applicationUrl: targetContent.value.applicationUrl
  })
    .then(() => {
      isApplyModalVisible.value = false
    })
}

const readBrands = (): Promise<void> => store.dispatch('companiesBrands/readBrands', companyUuid.value)
const readCompany = (): Promise<void> => store.dispatch('companiesCompany/readCompany', companyUuid.value)
const load = async(): Promise<void> => {
  await readBrands()
  await Promise.all([
    readBrands(),
    readCompany()
  ])
  readCompaniesBenefit({
    companyUuid: companyUuid.value,
    benefitId: benefitId.value,
  })
}
load()
</script>

<template>
  <KkBox>
    <template #header>
      <div class="benefitDetailPageHeader">
        <KkButton :text="t('pages.companies.benefits.backToIndex')" color="secondary" :to="{ name: 'CompaniesBenefitsIndex' }" />
      </div>
    </template>
    <template #content>
      <KkLoading v-if="isLoading" />
      <div v-else class="benefitDetailPageContent">
        <div class="benefitDetailPageContent__main">
          <div class="headSection">
            <div class="headSection__img">
              <img :src="benefit.service.iconUrl">
            </div>
            <div>
              <p class="serviceName">{{ benefit.service.name }}</p>
              <p class="companyName">{{ benefit.company.name }}</p>
            </div>
          </div>
          <h2 class="titleSection">
            {{ benefit.title }}
          </h2>
          <div class="eyeCatchSection">
            <img :src="benefit.eyeCatchUrl">
          </div>
          <div class="introductionSection">
            <div v-for="(introduction, introductionIndex) in benefit.introductions"
                 :key="'introductionIndex_' + introductionIndex"
                 class="introductionSection__item"
            >
              <template v-if="introduction.type === 'head'">
                <h3>{{ introduction.content }}</h3>
              </template>
              <template v-if="introduction.type === 'image_url'">
                <img :src="introduction.content">
              </template>
              <template v-if="introduction.type === 'body'">
                <p class="whitespacePreWrap">{{ introduction.content }}</p>
              </template>
            </div>
          </div>
          <KkBox
            :title="t('pages.companies.benefits.serviceMerit', { service: benefit.service.name })"
            border
            class="meritSection"
          >
            <template #content>
              <div v-for="(merit, meritIndex) in benefit.merits"
                   :key="'merit_' + meritIndex"
                   class="merit"
              >
                <div v-if="merit.imageUrl" class="merit__img">
                  <img :src="merit.imageUrl">
                </div>
                <div class="merit__content">
                  <h3 class="merit__head">{{ merit.head }}</h3>
                  <p class="whitespacePreWrap">{{ merit.body }}</p>
                </div>
              </div>
            </template>
          </KkBox>
          <KkBox
            :title="$t('pages.companies.benefits.planAndPrice')"
            border
            class="planAndPriceSection"
          >
            <template #content>
              <p class="planAndPriceDescription" v-html="benefit.planAndPriceDescription" />
            </template>
          </KkBox>
          <KkBox
            :title="t('pages.companies.benefits.serviceCompany.title')"
            border
            class="serviceCompanySection"
          >
            <template #content>
              <dl class="serviceCompany">
                <dt>
                  {{ t('pages.companies.benefits.serviceCompany.head1') }}
                </dt>
                <dd>
                  {{ benefit.company.name }}
                </dd>
                <dt>
                  {{ t('pages.companies.benefits.serviceCompany.head2') }}
                </dt>
                <dd>
                  {{ benefit.company.address }}
                </dd>
                <dt>
                  {{ t('pages.companies.benefits.serviceCompany.head3') }}
                </dt>
                <dd>
                  <a target="_blank" :href="benefit.company.siteUrl">
                    {{ benefit.company.siteUrl }}
                  </a>
                </dd>
              </dl>
            </template>
          </KkBox>
          <div class="buttonSection">
            <KkButton
              :text="t('pages.companies.benefits.buttons.service')"
              color="secondary"
              :href="benefit.service.url"
            />
            <KkButton
              v-if="benefit.service.academyUrl"
              :text="t('pages.companies.benefits.buttons.academy')"
              color="secondary"
              :href="benefit.service.academyUrl"
            />
          </div>
        </div>
        <div class="benefitDetailPageContent__side">
          <h2 class="sideTitle">
            {{ t(`pages.companies.benefits.sideMenu.${ isDocomoPlan ? 'upgradeTitle' : 'title' }`) }}
          </h2>

          <template v-for="(content, index) in benefit.contents" :key="index">
            <KkBox
              class="benefitContent"
              border
            >
              <template #content>
                <div class="benefitHeader">
                  <KkIcon name="benefit" class="benefitIcon" />
                  <h3 class="benefitTitle">
                    {{ content.title }}
                  </h3>
                </div>
                <div class="benefitFooter">
                  <p v-if="content.dueDate">
                    {{ t('pages.companies.benefits.sideMenu.applyDueDate', { dueDate: content.dueDate }) }}
                  </p>
                  <KkButton
                    class="benefitFooter__button"
                    text="詳しく見る"
                    @click="handleOpenApplyModal(content)"
                  />
                </div>
              </template>
            </KkBox>
          </template>
        </div>
      </div>
    </template>
  </KkBox>
  <KkApplyModal
    v-if="!isLoading && !brandLoading && targetContent"
    :is-visible="isApplyModalVisible"
    :benefit="benefit"
    :industry="targetBrandBusinessTypeNames.join(',')"
    :brand="targetBrands.map(brand => `${brand.attributes.name}(${brand.attributes.store_count}${t('labels.store')})`).join(',')"
    :applicant="currentUserName"
    :email="currentUserEmail"
    :company-name="company.name"
    :content="targetContent"
    :is-docomo-plan="isDocomoPlan"
    @close="isApplyModalVisible = false"
    @apply="handleApply"
  />
</template>
<style lang="scss" scoped>
@import '../../../../../stylesheets/biz_spa/foundation/var';

$sp: 768px;
@mixin sp {
  @media (max-width: ($sp)) {
    @content;
  }
}

.benefitDetailPageHeader {
  text-align: right;
}
.benefitDetailPageContent {
  display: flex;
  justify-content: space-between;
  width: 100%;
  &__main {
    width: 65%;
    max-width: 720px;
    margin: 0 auto;
  }
  &__side {
    width: 30%;
    max-width: 400px;
  }
  @include sp {
    flex-direction: column;
    &__main {
      width: 100%;
      max-width: 100%;
    }
    &__side {
      width: 100%;
      max-width: 100%;
    }
  }
}
.serviceName {
  font-weight: bold;
  font-size: 24px;
}
.companyName {
  font-weight: bold;
}
.headSection {
  display: flex;
  margin-bottom: 20px;
  &__img {
    margin-right: 32px;
    width: 120px;
  }
  @include sp {
    &__img {
      width: 75px;
      margin-right: 20px;
    }
  }
}
.eyeCatchSection {
  margin-bottom: 20px;
}
.titleSection {
  margin-bottom: 20px;
}
.introductionSection {
  margin-bottom: 32px;
  &__item {
    margin-bottom: 20px;
  }
}
.whitespacePreWrap {
  white-space: pre-wrap;
}
.meritSection {
  margin-bottom: 32px;
}
.planAndPriceSection {
  margin-bottom: 32px;
}
.merit {
  display: flex;
  margin-bottom: 20px;
  &__img {
    width: 50%;
    margin-right: 24px;
    + .merit__content {
      width: 50%;
    }
  }
  &__content {
    width: 100%;
  }
  &__head {
    margin-bottom: 8px;
  }
  @include sp {
    flex-direction: column;
    &:last-child {
      margin-bottom: 0;
    }
    &__img {
      width: 100%;
      margin-right: 0;
      margin-bottom: 6px;
      + .merit__content {
        width: 100%;
      }
    }
    &__content {
      width: 100%;
    }
  }
}
.serviceCompanySection {
  margin-bottom: 32px;
}
.serviceCompany {
  display: flex;
  flex-wrap: wrap;
  & > dt {
    width: 33%;
    margin-bottom: 8px;
    font-weight: bold;
  }
  & > dd {
    width: 66%;
    margin-bottom: 8px;
  }
}
.buttonSection {
  display: flex;
  justify-content: space-around;
  @include sp {
    flex-direction: column;
    margin-bottom: 32px;
    & > * + * {
      margin-top: 16px;
    }
  }
}
.benefitContent {
  margin-bottom: 10px;
}
.benefitHeader {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  @include sp {
    margin-bottom: 8px;
  }
}
.benefitFooter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  @include sp {
    flex-direction: column;
    align-items: flex-start;
  }
  &__button {
    margin-left: 16px;
    @include sp {
      margin-left: 0;
      margin-top: 5px;
    }
  }
}
.benefitIcon {
  width: 20px;
  min-width: 20px;
  margin-right: 8px;
  position: relative;
  top: 4px;
}
.benefitTitle {
  color: $color-accent-primary;
  font-size: 16px;
}
.sideTitle {
  margin-bottom: 10px;
  @include sp {
    margin-bottom: 5px;
  }
}
.benefitContentNote {
  margin-top: 16px;
  white-space: pre-wrap;
  color: $color-font-negative;
  ul {
    padding-left: 16px;
    li {
      list-style: initial;
      margin-bottom: 8px;
    }
  }
}
.planAndPriceDescription {
  white-space: pre-wrap;
}
</style>
